/* eslint-disable react/require-default-props */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa';

import { IconButton } from '../../../../../styles/ui/Button';
import { TextLink } from '../../../../../styles/ui/Link';
import {
   uploadResume,
   getResumeDownload,
} from '../../../../../services/hr/recruitment/candidatesApi';
import { recruitmentErrorMessage } from '../../utils/apiError';
import {
   UPLOAD_ACCEPTED_MIME_TYPES,
   validateUploadFile,
} from '../../utils/resume';
import { Fact, ResumeFactRow } from '../styles';

// ---------------------------------------------------------------------------
// Currículo compacto dentro do card "Identidade & contato" (substituiu o card
// dedicado — feedback 2026-07-14). Com currículo: hyperlink "Visualizar
// currículo ↗" (presigned S3 em nova aba) + botão refresh à direita pra
// substituir. Sem currículo: hyperlink "Anexar currículo" abre o seletor de
// arquivo. Upload dispara direto na seleção do arquivo (sem passo de confirmar
// — não há espaço pro fluxo em dois passos aqui).
// ---------------------------------------------------------------------------

export default function ResumeFact({
   candidateId,
   hasResume,
   resumeOriginalName,
   onUploaded,
}) {
   const fileInputRef = useRef(null);
   const [isUploading, setIsUploading] = useState(false);
   const [isOpening, setIsOpening] = useState(false);

   const handleFileChange = async (event) => {
      const file = event.target.files?.[0] ?? null;
      // Permite re-selecionar o mesmo arquivo depois de um erro.
      event.target.value = '';
      if (!file || isUploading) return;

      const validationError = validateUploadFile(file);
      if (validationError) {
         toast.error(validationError);
         return;
      }

      setIsUploading(true);
      try {
         const { message } = await uploadResume(candidateId, file);
         toast.success(message);
         onUploaded?.();
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, 'Erro ao enviar o currículo.'));
      } finally {
         setIsUploading(false);
      }
   };

   const handleView = async () => {
      if (isOpening) return;
      setIsOpening(true);
      try {
         const { url } = await getResumeDownload(candidateId);
         if (!url) {
            toast.error('Não foi possível gerar o link de visualização.');
            return;
         }
         window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao abrir o currículo.'),
         );
      } finally {
         setIsOpening(false);
      }
   };

   return (
      <Fact>
         <div className="fact-label">Currículo</div>
         <div className="fact-value">
            <input
               ref={fileInputRef}
               type="file"
               accept={UPLOAD_ACCEPTED_MIME_TYPES}
               style={{ display: 'none' }}
               onChange={handleFileChange}
            />

            {hasResume ? (
               <ResumeFactRow>
                  {/* Texto estático — a ação de abrir mora só no ícone. */}
                  <span
                     className="resume-view"
                     title={resumeOriginalName ?? undefined}
                  >
                     {isOpening ? 'Abrindo…' : 'Ver currículo'}
                     <IconButton
                        type="button"
                        onClick={handleView}
                        disabled={isOpening}
                        title="Abrir currículo em nova aba"
                        aria-label="Abrir currículo em nova aba"
                     >
                        <FaExternalLinkAlt size={13} />
                     </IconButton>
                  </span>
                  <IconButton
                     type="button"
                     onClick={() => fileInputRef.current?.click()}
                     disabled={isUploading}
                     title="Substituir currículo (PDF, JPEG, PNG ou WEBP — máx. 10MB)"
                     aria-label="Substituir currículo"
                  >
                     <FaSyncAlt className={isUploading ? 'spin' : undefined} />
                  </IconButton>
               </ResumeFactRow>
            ) : (
               <TextLink
                  as="button"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  title="PDF, JPEG, PNG ou WEBP — máx. 10MB"
               >
                  {isUploading ? 'Enviando…' : 'Anexar currículo'}
               </TextLink>
            )}
         </div>
      </Fact>
   );
}

ResumeFact.propTypes = {
   candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
   hasResume: PropTypes.bool,
   resumeOriginalName: PropTypes.string,
   onUploaded: PropTypes.func,
};
